import { FeatureFlag } from '@ps/web-core-enums';
import { useFeature } from '@ps/web-core-store';
import classNames from 'clsx';
import { isEmpty } from 'lodash';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import type { RouteComponentProps } from 'react-router';
import { Route, useParams } from 'react-router';
import { useLocation } from 'react-router-dom';

import Redirect from '@swf/components/Redirect/Redirect';

import useGetClubMemberOptions from '../../hooks/api/useGetClubMemberOptions';
import useCheckUserClubRole from '../../hooks/useCheckUserClubRole';
import useGetCurrentClub from '../../hooks/useGetCurrentClub';
import usePublicClubsRestricted from '../../hooks/usePublicClubsRestricted';
import { useHomeGamesWidget } from '../../model';
import { ClubRoute, UserClubRole } from '../../types/common';
import ClubNav from '../Nav/ClubNav/ClubNav';

import useAnnouncementStream from './Announcements/hooks/useAnnouncementStream';
import type { ClubContextState } from './ClubContext/ClubContext';
import ClubContext, { initialState } from './ClubContext/ClubContext';
import styles from './Container.scss';
import Header from './Header/Header';
import Lobby from './Lobby/Lobby';
import Members from './Members/MembersContainer';
import ResultsContainer from './Results/ResultsContainer';
import Settings from './Settings/Settings';
import Standings from './Standings/StandingsContainer';
import Wall from './Wall/Wall';
import useWallStream from './Wall/hooks/useWallStream';

export interface Props {
  path?: string;
  loading: boolean;
}

type ComponentType = Partial<{
  [K in ClubRoute]: React.FC<Props & RouteComponentProps>;
}>;

const Component: ComponentType = {
  [ClubRoute.Lobby]: Lobby,
  [ClubRoute.Results]: ResultsContainer,
  [ClubRoute.Members]: Members,
  [ClubRoute.Settings]: Settings,
  [ClubRoute.Standings]: Standings,
  [ClubRoute.Wall]: Wall
};

const Club: React.FC<Props> = ({ path }) => {
  const { widgetSelect } = useHomeGamesWidget();
  const { clubId, section } = useParams<{ clubId: string; section: ClubRoute }>();
  const { handleGetClubMemberOptions, loading: settingsLoading } = useGetClubMemberOptions();
  useWallStream({ clubId });
  useAnnouncementStream({ clubId });
  const PageComponent = Component[section];
  const location = useLocation();
  const { pathname } = location;
  const isWall = pathname.includes('/wall');
  const isMembers = pathname.includes('/members');
  const isLobby = pathname.includes('/lobby');
  const club = useGetCurrentClub(clubId);
  const restrictAccess = useCheckUserClubRole(`${clubId}`, [
    UserClubRole.Confirmation,
    UserClubRole.Suspended
  ]);
  const isPublicClubJoinAndCreateEnabled = useFeature(
    FeatureFlag.PKRHomeGamesJoinAndCreatePublicClubs
  );
  const clubOptions = useSelector(widgetSelect.clubOptionsById);
  const { showClubRestrictedToast } = usePublicClubsRestricted();

  useEffect(() => {
    handleGetClubMemberOptions(Number(clubId));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clubId]);

  useEffect(() => {
    if (!isPublicClubJoinAndCreateEnabled && clubOptions?.[clubId]?.open)
      showClubRestrictedToast('/homegames/clubs');
  }, [clubOptions, clubId, isPublicClubJoinAndCreateEnabled, showClubRestrictedToast]);

  if (isEmpty(club) || restrictAccess || !PageComponent) {
    return <Redirect alias="homeGamesClubs" />;
  }

  return (
    <>
      <Header />
      <ClubNav />
      <div
        className={classNames(styles.container, {
          [`club-section-${section}`]: true,
          [styles.membersContainer]: isMembers,
          [styles.wallContainer]: isWall,
          [styles.lobbyContainer]: isLobby
        })}
      >
        <Route
          path={path}
          render={(props) => <PageComponent {...props} loading={settingsLoading} />}
        />
      </div>
    </>
  );
};

const Container: React.FC<Props> = (props) => {
  const [state, setState] = useState<ClubContextState>(initialState);

  return (
    <ClubContext.Provider value={{ state, setState }}>
      <Club {...props} />
    </ClubContext.Provider>
  );
};

export default Container;
